import { useState, type ReactNode } from 'react';
import {
  BooleanResponse,
  DateResponse,
  MultiResponse,
  NumberResponse,
  SelectResponse,
  TextResponse,
} from './responses';
import { ThemeToggle } from './ThemeToggle';
import './Storyboard.css';

/** Storyboard — a visual catalog of every response component. Each scene
 *  shows one usage pattern (standalone, controlled, hybrid, etc.) with a
 *  live output panel that displays whatever the component last submitted.
 *
 *  Served at `/?storyboard=1` and fully isolated from the intake flow. */
export function Storyboard() {
  return (
    <main id="main" className="cf-storyboard">
      <header className="cf-storyboard__header">
        <div>
          <div className="cf-storyboard__eyebrow">CivicForce · Phase 0</div>
          <h1 className="cf-storyboard__title">Response Component Storyboard</h1>
          <p className="cf-storyboard__subtitle">
            Every constrained-input control that can replace the chat text
            field — or sit alongside it. Each scene shows the component with
            realistic props and a live output pane that displays the last
            value submitted.
          </p>
        </div>
        <ThemeToggle />
      </header>

      <div className="cf-storyboard__grid">
        <Scene
          number="01"
          title="TextResponse — single-line"
          summary="Plain short-answer input. Fires onSubmit on Enter or the send button. Shown with autocomplete/inputMode hints for a mobile-friendly keyboard."
          when="Open fields like mailing address, phone number, or a free-form correction during upload review."
        >
          {(setOut) => (
            <TextResponse
              label="Street address"
              multiline={false}
              autoComplete="street-address"
              placeholder="88 Pine Rd"
              onSubmit={setOut}
            />
          )}
        </Scene>

        <Scene
          number="02"
          title="TextResponse — multi-line"
          summary="Default variant for the chat free-text flow. Shift+Enter inserts a newline; Enter submits."
          when="Chat mode when no constrained question is active — the user types whatever they want."
        >
          {(setOut) => (
            <TextResponse
              label="Tell me a little more"
              multiline
              rows={3}
              placeholder="Whatever you'd like to share…"
              onSubmit={setOut}
            />
          )}
        </Scene>

        <Scene
          number="03"
          title="SelectResponse — stacked radios"
          summary="Single-choice with one option per row. 44px tap target on every row; selected option highlights with a border + tinted background, not color alone."
          when="Enum fields like housing status, employment status, education level — any time exactly one choice applies."
        >
          {(setOut) => (
            <SelectResponse
              label="Current housing situation"
              options={[
                { key: 'renting', label: 'Renting' },
                { key: 'owning', label: 'Owning' },
                { key: 'with_family', label: 'Staying with family' },
                { key: 'unhoused', label: 'Unhoused' },
              ]}
              onSubmit={(key) => setOut(key)}
            />
          )}
        </Scene>

        <Scene
          number="04"
          title="SelectResponse — chip layout"
          summary="Same component, horizontal chips. Use when option labels are short and fit on one screen without scrolling."
          when="Preferred language picker, sex at birth, veteran status — short enum sets where density is an asset."
        >
          {(setOut) => (
            <SelectResponse
              label="Preferred language"
              layout="chips"
              options={[
                { key: 'en', label: 'English' },
                { key: 'es', label: 'Español' },
                { key: 'pt-BR', label: 'Português' },
                { key: 'other', label: 'Other' },
              ]}
              onSubmit={(key) => setOut(key)}
            />
          )}
        </Scene>

        <Scene
          number="05"
          title="MultiResponse — checkbox list"
          summary="Pick zero or more. `minSelections` keeps submit disabled until the threshold is met (default 1)."
          when="Race (PIRL allows multiple), program interest signals, required document types."
        >
          {(setOut) => (
            <MultiResponse
              label="Which apply to you?"
              options={[
                { key: 'american_indian', label: 'American Indian / Alaska Native' },
                { key: 'asian', label: 'Asian' },
                { key: 'black', label: 'Black or African American' },
                { key: 'pacific_islander', label: 'Native Hawaiian / Pacific Islander' },
                { key: 'white', label: 'White' },
                { key: 'prefer_not_to_say', label: 'Prefer not to say' },
              ]}
              onSubmit={(keys) => setOut(keys.join(', '))}
            />
          )}
        </Scene>

        <Scene
          number="06"
          title="BooleanResponse — immediate"
          summary="Yes / No pair with a checkmark and an X glyph so the selection is unmistakable regardless of color perception. Immediate-submit: tapping Yes fires onSubmit(true) at once."
          when="Quick confirmations — MSFW (farm work) status, Rapid Response attendance, consent boxes."
        >
          {(setOut) => (
            <BooleanResponse
              label="Have you done any farm or food-processing work in the past 12 months?"
              onSubmit={(v) => setOut(v ? 'yes' : 'no')}
            />
          )}
        </Scene>

        <Scene
          number="07"
          title="BooleanResponse — staged"
          summary="Same component, but `stage` forces a second click on the submit button before the value commits. Use this when the consequence is permanent (data deletion, final submission)."
          when="Irreversible confirmations — the 'are you sure?' style moment before intake submit."
        >
          {(setOut) => (
            <BooleanResponse
              label="Submit this application now?"
              stage
              submitLabel="Submit"
              onSubmit={(v) => setOut(v ? 'submitted' : 'cancelled')}
            />
          )}
        </Scene>

        <Scene
          number="08"
          title="DateResponse — native picker"
          summary="Native HTML5 date input — iOS shows the OS wheel, Android opens the calendar, desktop gets the browser popup. Value is always ISO YYYY-MM-DD. `min` and `max` bound the picker."
          when="Date of birth, intake date, layoff date — anywhere a calendar answer is required."
        >
          {(setOut) => (
            <DateResponse
              label="Date of birth"
              max="2026-04-18"
              onSubmit={setOut}
            />
          )}
        </Scene>

        <Scene
          number="09"
          title="NumberResponse — bounded with affix"
          summary="Numeric input with optional min/max, step, and visible prefix/suffix. Submit stays disabled until the typed value is in range; aria-invalid flips on for assistive tech."
          when="Income amounts (with $ prefix), household size, years of experience, hours per week."
        >
          {(setOut) => (
            <NumberResponse
              label="Monthly household income"
              prefix="$"
              min={0}
              max={25000}
              placeholder="2400"
              onSubmit={(n) => setOut(`$${n.toLocaleString()}`)}
            />
          )}
        </Scene>

        <Scene
          number="10"
          title="Hybrid — DateResponse + TextResponse fallback"
          summary="Primary constrained control on top, free-text fallback below. `hideSubmit` on both so the parent renders a single shared action. Parent mirrors each child's onChange so it can tell which one the user intends."
          when="Date of birth, when we want the calendar but need to accept natural-language answers like 'April 12, 1983' for citizens who aren't comfortable with pickers."
        >
          {(setOut) => <HybridDateScene onResolve={setOut} />}
        </Scene>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Scene wrapper — common frame around every component demo. Shows the
// component's last-submitted value in a live output panel.
// ---------------------------------------------------------------------------

interface SceneProps {
  number: string;
  title: string;
  summary: string;
  when: string;
  children: (setOutput: (s: string) => void) => ReactNode;
}

function Scene({ number, title, summary, when, children }: SceneProps) {
  const [output, setOutput] = useState<string | null>(null);
  return (
    <section className="cf-scene">
      <header className="cf-scene__header">
        <div className="cf-scene__number">{number}</div>
        <div>
          <h2 className="cf-scene__title">{title}</h2>
          <p className="cf-scene__summary">{summary}</p>
        </div>
      </header>
      <div className="cf-scene__body">
        <div className="cf-scene__component">{children(setOutput)}</div>
        <aside className="cf-scene__side">
          <div className="cf-scene__label">Typical use</div>
          <p className="cf-scene__when">{when}</p>
          <div className="cf-scene__label">Last submitted value</div>
          {output === null ? (
            <div className="cf-scene__output cf-scene__output--empty">
              <span aria-hidden="true">—</span>
              <span className="cf-visually-hidden">nothing submitted yet</span>
            </div>
          ) : (
            <div className="cf-scene__output" role="status" aria-live="polite">
              {output}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hybrid scene: two controls, one shared submit.
// Shows parents how to compose response components into richer flows.
// ---------------------------------------------------------------------------

function HybridDateScene({ onResolve }: { onResolve: (s: string) => void }) {
  const [pickedDate, setPickedDate] = useState<string>('');
  const [typedFallback, setTypedFallback] = useState<string>('');

  const canCommit = pickedDate.length > 0 || typedFallback.trim().length > 0;

  function commit() {
    if (pickedDate) onResolve(`picker → ${pickedDate}`);
    else onResolve(`typed → "${typedFallback.trim()}"`);
  }

  return (
    <div className="cf-hybrid">
      <DateResponse
        label="Pick a date"
        onChange={setPickedDate}
        hideSubmit
      />
      <div className="cf-hybrid__divider" aria-hidden="true">
        or
      </div>
      <TextResponse
        label="Type it in your own words"
        multiline={false}
        placeholder="April 12, 1983"
        onChange={setTypedFallback}
        hideSubmit
      />
      <div className="cf-hybrid__actions">
        <button
          type="button"
          className="cf-btn cf-btn--primary"
          onClick={commit}
          disabled={!canCommit}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
